import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { INDIAN_STATES } from "@/lib/indian_states";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronsUpDown, Plus } from "lucide-react";

/**
 * Pair of pickers used wherever we need an Indian state + district/city
 * field on a contact-style record.
 *
 * Props:
 *   - state: current state value (string | "")
 *   - district: current district value (string | "")
 *   - onChange: ({state, district}) => void   — called whenever EITHER value changes
 *   - testId: optional prefix for data-testid (defaults to "state-district")
 *
 * Behaviour:
 *   - State is a vanilla dropdown of 28 states + 8 UTs.
 *   - District is a searchable combobox: types filter the known list (loaded
 *     from /api/marketing-contacts/districts?state=…), and a free-form
 *     entry can be added on the fly. Existing values that don't match a
 *     suggestion are still preserved.
 *   - Changing the state CLEARS the district (it almost certainly won't
 *     belong to the new state) — parent gets one `onChange` call with both
 *     new values so we don't temporarily desync.
 */
export default function StateDistrictPicker({ state = "", district = "", onChange, testId = "state-district", layout = "grid" }) {
  const [districts, setDistricts] = useState([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!state) { setDistricts([]); return; }
    let cancelled = false;
    api
      .get("/marketing-contacts/districts", { params: { state } })
      .then((r) => { if (!cancelled) setDistricts(r.data?.districts || []); })
      .catch(() => { if (!cancelled) setDistricts([]); });
    return () => { cancelled = true; };
  }, [state]);

  const filteredDistricts = useMemo(() => {
    const ql = query.trim().toLowerCase();
    if (!ql) return districts;
    return districts.filter((d) => d.toLowerCase().includes(ql));
  }, [districts, query]);

  // True when the typed query matches no existing district AND isn't blank —
  // surfaces the "Add new" hint inside the popover.
  const canAddNew =
    query.trim().length > 0 &&
    !districts.some((d) => d.toLowerCase() === query.trim().toLowerCase());

  const pickDistrict = (val) => {
    onChange({ state, district: val });
    setOpen(false);
    setQuery("");
  };

  const onStateChange = (v) => {
    // Clear district whenever the state changes — almost certainly won't
    // belong to the new state. Parent receives both values in one call.
    onChange({ state: v, district: "" });
  };

  const containerCls =
    layout === "stack" ? "space-y-3" : "grid grid-cols-2 gap-3";

  return (
    <div className={containerCls}>
      <div>
        <Label className="text-xs">State</Label>
        <Select value={state || undefined} onValueChange={onStateChange}>
          <SelectTrigger className="rounded-2xl mt-1" data-testid={`${testId}-state`}>
            <SelectValue placeholder="Select state" />
          </SelectTrigger>
          <SelectContent className="rounded-2xl max-h-80">
            {INDIAN_STATES.map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs">District / city</Label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={!state}
              data-testid={`${testId}-district`}
              className="rounded-2xl mt-1 w-full justify-between font-normal h-10"
            >
              <span className={district ? "" : "text-muted-foreground"}>
                {district || (state ? "Search or add district / city" : "Pick state first")}
              </span>
              <ChevronsUpDown className="h-4 w-4 opacity-50 ml-2 shrink-0" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="p-0 rounded-2xl w-[var(--radix-popover-trigger-width)]" align="start">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Type to search or add…"
                value={query}
                onValueChange={setQuery}
                data-testid={`${testId}-district-search`}
              />
              <CommandList>
                {filteredDistricts.length === 0 && !canAddNew && (
                  <CommandEmpty>No matches. Type to add a new one.</CommandEmpty>
                )}
                {filteredDistricts.length > 0 && (
                  <CommandGroup heading="Existing">
                    {filteredDistricts.map((d) => (
                      <CommandItem
                        key={d}
                        value={d}
                        onSelect={() => pickDistrict(d)}
                        data-testid={`${testId}-district-option-${d}`}
                      >
                        <Check className={`mr-2 h-4 w-4 ${district === d ? "opacity-100" : "opacity-0"}`} />
                        {d}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                {canAddNew && (
                  <CommandGroup heading="Add new">
                    <CommandItem
                      value={`__add_${query}`}
                      onSelect={() => pickDistrict(query.trim())}
                      data-testid={`${testId}-district-add`}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add &ldquo;{query.trim()}&rdquo;
                    </CommandItem>
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
